import { useCallback, useMemo, useRef, useState } from "react";

/**
 * Multi-step undo/redo stack scoped to a single editor session
 * (M7.5 C2; redo added with the direct-manipulation UX pass).
 *
 * Cap at 10 entries per direction — beyond which the oldest snapshot
 * gets dropped. The stacks are NOT persisted: switching source
 * (tactical board ↔ telestrator), opening a different clip-edit
 * context, or remounting the editor wipes them. That's intentional —
 * undoing across an unrelated edit context would be confusing (the
 * M7 modal-stacking lesson applied to history).
 *
 * Usage:
 *   const stack = useUndoStack<BoardScene>({ maxDepth: 10 });
 *   stack.push(currentScene);            // before applying a change
 *                                        // (clears the redo branch)
 *   const prev = stack.undo(currentScene); // current → redo, returns
 *                                          // the snapshot to restore
 *   const next = stack.redo(currentScene); // current → undo, returns
 *                                          // the snapshot to restore
 *   stack.clear();                        // on source / context switch
 *
 * `undo`/`redo` take the CURRENT value so the caller doesn't have to
 * push it separately — and returning the snapshot (rather than
 * calling a `restore` callback) lets the caller decide whether it
 * can still be applied.
 */

export type UndoStack<T> = {
  /** Push a snapshot before a user-initiated write. Drops the oldest
   * if at cap. Clears the redo branch — a fresh edit invalidates it. */
  push: (snapshot: T) => void;
  /** Pop the most-recent undo snapshot, moving `current` onto the
   * redo stack. Returns `undefined` when there's nothing to undo. */
  undo: (current: T) => T | undefined;
  /** Pop the most-recent redo snapshot, moving `current` back onto
   * the undo stack. Returns `undefined` when there's nothing to redo. */
  redo: (current: T) => T | undefined;
  /** Wipe both stacks — call on context switches. */
  clear: () => void;
  /** Undo entries — drives the Undo button's disabled state. */
  depth: number;
  /** Redo entries — drives the Redo button's disabled state. */
  redoDepth: number;
};

type Options = {
  /** Default 10. */
  maxDepth?: number;
};

export function useUndoStack<T>(opts: Options = {}): UndoStack<T> {
  const maxDepth = opts.maxDepth ?? 10;
  // The stacks live in refs so the callbacks are stable across
  // renders. Separate depth states re-render consumers (the buttons'
  // `disabled` state) when the stacks change.
  const undoRef = useRef<T[]>([]);
  const redoRef = useRef<T[]>([]);
  const [depth, setDepth] = useState(0);
  const [redoDepth, setRedoDepth] = useState(0);

  const push = useCallback(
    (snapshot: T) => {
      const next = [...undoRef.current, snapshot];
      if (next.length > maxDepth) next.shift();
      undoRef.current = next;
      setDepth(next.length);
      redoRef.current = [];
      setRedoDepth(0);
    },
    [maxDepth],
  );

  const undo = useCallback((current: T): T | undefined => {
    const stack = undoRef.current;
    if (stack.length === 0) return undefined;
    const prev = stack[stack.length - 1];
    undoRef.current = stack.slice(0, -1);
    setDepth(undoRef.current.length);
    redoRef.current = [...redoRef.current, current];
    setRedoDepth(redoRef.current.length);
    return prev;
  }, []);

  const redo = useCallback(
    (current: T): T | undefined => {
      const stack = redoRef.current;
      if (stack.length === 0) return undefined;
      const next = stack[stack.length - 1];
      redoRef.current = stack.slice(0, -1);
      setRedoDepth(redoRef.current.length);
      const undoNext = [...undoRef.current, current];
      if (undoNext.length > maxDepth) undoNext.shift();
      undoRef.current = undoNext;
      setDepth(undoRef.current.length);
      return next;
    },
    [maxDepth],
  );

  const clear = useCallback(() => {
    undoRef.current = [];
    redoRef.current = [];
    setDepth(0);
    setRedoDepth(0);
  }, []);

  // Memoized so consumers can put the stack object in effect dep
  // arrays without re-registering listeners on every render — the
  // identity changes only when a depth actually changes.
  return useMemo(
    () => ({ push, undo, redo, clear, depth, redoDepth }),
    [push, undo, redo, clear, depth, redoDepth],
  );
}
